import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FeatureKey, MeResponse, MembershipSummary } from '@gbv/types';
import { api, setAccessToken, setActiveOrgId } from '../lib/api';

/**
 * Auth and the active organisation. Server data never lives here — that is
 * TanStack Query's job. This store holds identity, the org the user is looking
 * at, and nothing else.
 *
 * Only the chosen org id is persisted. The access token is deliberately not,
 * so a closed tab means a fresh token on return.
 */

interface AuthState {
  user: MeResponse['user'] | null;
  memberships: MembershipSummary[];
  activeOrgId: string | null;
  status: 'unknown' | 'authenticated' | 'anonymous';
  /** True when an admin opened this session through "View As". Writes are refused. */
  isReadOnly: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
  switchOrg: (orgId: string) => void;

  activeMembership: () => MembershipSummary | null;
  can: (feature: FeatureKey) => boolean;
  canDelete: (feature: FeatureKey) => boolean;
}

interface LoginResponse extends MeResponse {
  accessToken: string;
  expiresIn: number;
}

function applySession(
  set: (partial: Partial<AuthState>) => void,
  get: () => AuthState,
  payload: LoginResponse | MeResponse,
): void {
  if ('accessToken' in payload) setAccessToken(payload.accessToken);

  const memberships = payload.memberships;
  const previous = get().activeOrgId;

  // Keep the chosen org across a refresh when it is still a valid membership.
  const nextOrgId =
    previous && memberships.some((m) => m.orgId === previous)
      ? previous
      : memberships[0]?.orgId ?? null;

  setActiveOrgId(nextOrgId);

  set({
    user: payload.user,
    memberships,
    activeOrgId: nextOrgId,
    status: 'authenticated',
  });
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      memberships: [],
      activeOrgId: null,
      status: 'unknown',
      isReadOnly: false,

      login: async (email, password) => {
        const payload = await api.post<LoginResponse>(
          '/auth/login',
          { email, password },
          { skipOrg: true },
        );
        applySession(set, get, payload);
      },

      logout: async () => {
        await api.post('/auth/logout', undefined, { skipOrg: true }).catch(() => {
          // A failed logout still clears the client; the cookie expires anyway.
        });
        setAccessToken(null);
        setActiveOrgId(null);
        set({
          user: null,
          memberships: [],
          activeOrgId: null,
          status: 'anonymous',
          isReadOnly: false,
        });
      },

      /** Called once on boot: exchanges the refresh cookie for a live session. */
      restore: async () => {
        // An admin's "View As" link arrives as #impersonate=<token>. Adopt it
        // instead of the refresh cookie, and strip it from the address bar so
        // the token is not left in history or copied out of the URL.
        const match = /#impersonate=([\w.-]+)/.exec(window.location.hash);
        if (match?.[1]) {
          setAccessToken(match[1]);
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          try {
            const me = await api.get<MeResponse>('/me', { skipOrg: true });
            applySession(set, get, me);
            set({ isReadOnly: true });
            return;
          } catch {
            setAccessToken(null);
          }
        }

        try {
          const payload = await api.post<LoginResponse>('/auth/refresh', undefined, {
            skipOrg: true,
          });
          applySession(set, get, payload);
          set({ isReadOnly: false });
        } catch {
          setAccessToken(null);
          set({ user: null, memberships: [], status: 'anonymous', isReadOnly: false });
        }
      },

      switchOrg: (orgId) => {
        if (!get().memberships.some((m) => m.orgId === orgId)) return;
        setActiveOrgId(orgId);
        set({ activeOrgId: orgId });
      },

      activeMembership: () => {
        const { memberships, activeOrgId } = get();
        return memberships.find((m) => m.orgId === activeOrgId) ?? null;
      },

      /**
       * Mirrors the backend decision so the UI can hide what the person cannot
       * use. The backend still returns 403 — this is a courtesy, not a control.
       */
      can: (feature) => {
        const membership = get().activeMembership();
        return membership?.featureAccess?.[feature] === true;
      },

      /**
       * The delete keys, mirroring requireDelete on the API — including the
       * part where an admin passes on their role.
       *
       * featureAccess is written once when a membership is created, so an
       * admin whose membership predates a given key does not hold it, and
       * can() alone would hide a button the API would happily honour.
       */
      canDelete: (feature) => {
        const membership = get().activeMembership();
        if (!membership) return false;
        if (membership.role === 'SUB_ADMIN' || membership.role === 'SUPER_ADMIN') return true;
        return membership.featureAccess?.[feature] === true;
      },
    }),
    {
      name: 'gbv-employee-shell',
      partialize: (state) => ({ activeOrgId: state.activeOrgId }),
    },
  ),
);
